<template>
  <div class="space-y-5">
    <!-- <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold text-gray-800">Login with OTP</h3>
      <button
        type="button"
        @click="$emit('cancel')"
        class="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline"
      >
        Use password instead
      </button>
    </div> -->

    <!-- Request OTP -->
    <div
      v-if="!otpSent"
      class="space-y-4"
    >
      <div class="space-y-1">
        <!-- <label
          for="identifier"
          class="block text-sm font-semibold text-gray-700 mb-1"
        >
          Email or Phone
        </label> -->
        <input
          ref="identifierInputRef"
          id="identifier"
          v-model.trim="identifier"
          type="text"
          placeholder="Enter email or phone number"
          class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-300"
        />
      </div>

      <transition name="fade">
        <div
          v-if="error"
          class="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3"
        >
          <span>{{ error }}</span>
        </div>
      </transition>
      <transition name="fade">
        <div
          v-if="info"
          class="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-3"
        >
          {{ info }}
        </div>
      </transition>

      <button
        @click="requestOtp"
        :disabled="isRequesting || !identifier"
        class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <span
          v-if="isRequesting"
          class="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin"
        ></span>
        <span>{{ isRequesting ? "Sending OTP..." : "Send OTP" }}</span>
      </button>
    </div>

    <!-- Verify OTP -->
    <div
      v-else
      class="space-y-4"
    >
      <div
        class="rounded-xl border border-green-200 bg-green-50 text-green-800 p-3 text-sm"
      >
        OTP sent to {{ maskedTarget }}. Enter the 6-digit code below.
      </div>

      <div class="space-y-1">
        <label
          for="otp"
          class="block text-sm font-semibold text-gray-700 mb-1"
        >
          One-Time Password
        </label>
        <input
          ref="otpInputRef"
          id="otp"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="6"
          v-model.trim="otp"
          autocomplete="one-time-code"
          placeholder="Enter 6-digit code"
          class="w-full px-4 py-3 tracking-widest text-center text-lg border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-300"
        />
      </div>

      <div class="flex items-center justify-between text-sm text-gray-600">
        <button
          type="button"
          class="text-blue-600 hover:text-blue-800 font-semibold hover:underline"
          @click="resetToRequest"
        >
          Edit input
        </button>

        <button
          type="button"
          class="text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
          :disabled="resendCooldown > 0 || isRequesting"
          @click="resendOtp"
        >
          Resend OTP
          <span v-if="resendCooldown > 0">({{ resendCooldown }}s)</span>
        </button>
      </div>

      <transition name="fade">
        <div
          v-if="error"
          class="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3"
        >
          <span>{{ error }}</span>
        </div>
      </transition>

      <button
        @click="verifyOtp"
        :disabled="isVerifying || otp.length !== 6"
        class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <span
          v-if="isVerifying"
          class="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin"
        ></span>
        <span>{{ isVerifying ? "Verifying..." : "Verify & Sign In" }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onBeforeUnmount, watch, nextTick } from "vue";

  const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

  const props = defineProps({
    requestEndpoint: { type: String, default: "/api/auth/login/send-otp" },
    verifyEndpoint: { type: String, default: "/api/auth/login/verify-otp" },
  });

  const emit = defineEmits(["success", "cancel"]);

  const identifier = ref(""); // email or phone
  const otp = ref("");
  const otpSent = ref(false);
  const target = ref("");

  const isRequesting = ref(false);
  const isVerifying = ref(false);
  const error = ref("");
  const info = ref("");
  const otpInputRef = ref(null);
  const identifierInputRef = ref(null);

  const maskedTarget = computed(() => {
    if (!target.value) return "";
    if (target.value.includes("@"))
      return target.value.replace(/(.{2}).+(@.+)/, "$1****$2");
    return target.value.replace(/(\+\d{2})\d+(\d{2})/, "$1******$2");
  });

  let timer = null;
  const resendCooldown = ref(60);
  const startCooldown = () => {
    clearInterval(timer);
    resendCooldown.value = 60;
    timer = setInterval(() => {
      resendCooldown.value -= 1;
      if (resendCooldown.value <= 0) clearInterval(timer);
    }, 1000);
  };
  onBeforeUnmount(() => clearInterval(timer));

  const resetToRequest = async () => {
    otpSent.value = false;
    otp.value = "";
    error.value = "";
    info.value = "";
    clearInterval(timer);
    resendCooldown.value = 0;

    await nextTick();
    identifierInputRef.value?.focus();
  };

  const requestOtp = async () => {
    error.value = "";
    info.value = "";

    if (!identifier.value) {
      error.value = "Please enter your email or phone number.";
      return;
    }

    try {
      isRequesting.value = true;

      const res = await fetch(`${baseURL}${props.requestEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: identifier.value.includes("@") ? identifier.value : undefined,
          phone: identifier.value.includes("@") ? undefined : identifier.value,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to send OTP.");

      target.value = identifier.value;
      const firstTime = !otpSent.value;

      otpSent.value = true;
      info.value = data?.message || "OTP sent successfully.";
      startCooldown();

      if (!firstTime) {
        await nextTick();
        setTimeout(() => {
          otpInputRef.value?.focus();
        }, 50);
      }
    } catch (e) {
      error.value = e.message || "Failed to send OTP. Please try again.";
    } finally {
      isRequesting.value = false;
    }
  };

  const resendOtp = async () => {
    if (resendCooldown.value > 0) return;
    await requestOtp();
  };

  const verifyOtp = async () => {
    error.value = "";
    if (otp.value.length !== 6) {
      error.value = "Please enter the 6-digit OTP.";
      return;
    }

    try {
      isVerifying.value = true;

      const res = await fetch(`${baseURL}${props.verifyEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: identifier.value.includes("@") ? identifier.value : undefined,
          phone: identifier.value.includes("@") ? undefined : identifier.value,
          otp: otp.value,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "OTP verification failed.");

      emit("success", { token: data.token, user: data.user });
    } catch (e) {
      error.value = e.message || "OTP verification failed. Please try again.";
    } finally {
      isVerifying.value = false;
    }
  };
  watch(otp, async (value) => {
    if (value.length === 6 && otpSent.value && !isVerifying.value) {
      await verifyOtp();
    }
  });
  watch(otpSent, async (val) => {
    if (val) {
      await nextTick();
      setTimeout(() => {
        otpInputRef.value?.focus();
      }, 50);
    }
  });
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.2s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
  }
</style>
